import React, { useState, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { ImageDropzone } from 'react-file-utils';

import useToast from '../../../../../../hooks/useToast';
import MessageInputContext from '../../../../../../../chat/context/MessageInputContext';
import useStorage from '../../../../../../../hooks/useStorage';
import MessageInput from '../../../../../../../chat/components/MessageInput';
import AttachmentsPreview from '../../../../../../../chat/components/ChannelMessageInput/components/AttachmentsPreview';
import { getProcessedAttachmentData } from '../../../../../../../chat/utils/attachments';

import texts from './texts.json';
import {
  MAX_ATTACHMENTS_COUNT,
  buildFilesToAddFromAttachments,
  getCommentAttachmentsStoragePath,
  processUploadResults,
} from './utils';

const CommentInput = ({
  text,
  onChange,
  onSubmit,
  isSendingComment,
  isEditComment,
  inputRef,
  coachDoc,
  isReplyOverLimit,
}) => {
  const [commentAttachments, setCommentAttachments] = useState([]);

  const { onAddAttachments } = useContext(MessageInputContext);
  const { uploadAttachments, deleteAttachments } = useStorage();

  const { showToast } = useToast();

  const processCommentAttachments = useCallback(async (attachmentsToUpload) => {
    if (!attachmentsToUpload.length) return [];

    const { filesToAdd, filesToAddById } = buildFilesToAddFromAttachments(attachmentsToUpload);
    const storagePath = getCommentAttachmentsStoragePath(coachDoc.id);

    const results = await uploadAttachments(filesToAdd, storagePath);
    const { uploadedAttachments, failedFiles } = processUploadResults(results);

    if (failedFiles.length > 0) {
      if (uploadedAttachments.length) {
        const filesToDelete = uploadedAttachments.map(({ fileRef }) => ({ storagePath: fileRef }));
        await deleteAttachments(filesToDelete);
      }
      throw new Error(format(texts.failedToUploadFiles, { count: failedFiles.length }));
    }

    return uploadedAttachments.map(({
      fileRef,
      metadata: { contentType, size },
      fileId,
    }) => getProcessedAttachmentData({
      fileName: filesToAddById[fileId].fileName,
      contentType,
      pathRef: fileRef,
      fileSize: size,
    }));
  }, [coachDoc.id, uploadAttachments, deleteAttachments]);

  const handleSubmit = useCallback(async () => {
    const processedAttachments = commentAttachments.length
      ? await processCommentAttachments(commentAttachments)
      : [];
    await onSubmit(processedAttachments);
    setCommentAttachments([]);
  }, [commentAttachments, processCommentAttachments, onSubmit]);

  const handleAddAttachments = useCallback((fileList) => {
    const overLimit = commentAttachments.length + Object.values(fileList).length > MAX_ATTACHMENTS_COUNT;
    if (overLimit) {
      showToast(format(texts.maxFilesError, { max: MAX_ATTACHMENTS_COUNT }), { error: true });
      return;
    }
    onAddAttachments(fileList, setCommentAttachments, true);
  }, [onAddAttachments, showToast, commentAttachments.length]);

  const onRemoveAttachment = useCallback((attachmentId) => {
    setCommentAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  }, []);

  const isDisabled = isSendingComment
    || (text.trim().length === 0 && commentAttachments.length === 0)
    || isReplyOverLimit;

  return (
    <ImageDropzone
      handleFiles={handleAddAttachments}
      multiple
    >
      <AttachmentsPreview
        attachments={commentAttachments}
        onRemove={onRemoveAttachment}
        onAddAttachments={handleAddAttachments}
      />
      <MessageInput
        text={text}
        onChange={onChange}
        placeholder={texts.reply}
        onSubmit={handleSubmit}
        isDisabled={isDisabled}
        isSendingMessage={isSendingComment}
        inputRef={inputRef}
        useExtras
        maxRows={2}
        framePadding={0}
        onAddAttachments={handleAddAttachments}
        isEditMessage={isEditComment}
        hideVoiceRecording
        showSubmitInEditMode
      />
    </ImageDropzone>
  );
};

CommentInput.propTypes = {
  text: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSendingComment: PropTypes.bool.isRequired,
  isEditComment: PropTypes.bool.isRequired,
  inputRef: PropTypes.object,
  coachDoc: PropTypes.object.isRequired,
  isReplyOverLimit: PropTypes.bool.isRequired,
};

CommentInput.defaultProps = {
  inputRef: {},
};

export default CommentInput;
